import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import BadgeIcon from "@mui/icons-material/Badge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import * as XLSX from "xlsx";
// @ts-ignore - react-datepicker type declarations are not installed in this project.
import DatePicker from "react-datepicker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "react-datepicker/dist/react-datepicker.css";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import PageContainer from "../../../components/container/PageContainer";
import {
  GetDeviceData,
  GetOrganizations,
  ImportDeviceData,
} from "../../../services/asai";
import ExcelJS from "exceljs";
dayjs.extend(customParseFormat);

export type DeviceAttendanceRow = {
  personId: string;
  personName: string;
  organization: string;
  date: string;
  time: string;
};

type Filters = {
  startDate: Date | null;
  endDate: Date | null;
  organization: string;
  personId: string;
  personName: string;
};

const INITIAL_FILTERS: Filters = {
  startDate: null,
  endDate: null,
  organization: "",
  personId: "",
  personName: "",
};

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];

const DEVICE_COLUMNS: Array<
  [string, keyof DeviceAttendanceRow]
> = [
    ["Person ID", "personId"],
    ["Person Name", "personName"],
    ["Person Organization", "organization"],
    ["Date", "date"],
    ["Time", "time"],
  ];

const BCrumb = [
  {
    to: "/",
    title: "ASAI",
  },
  {
    title: "Device Data",
  },
];

function TablePaginationActions(props: {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    page: number,
  ) => void;
}) {
  const theme = useTheme();

  const {
    count,
    page,
    rowsPerPage,
    onPageChange,
  } = props;

  const lastPage = Math.max(
    0,
    Math.ceil(count / rowsPerPage) - 1,
  );

  return (
    <Box
      sx={{
        flexShrink: 0,
        ml: 1,
        display: "flex",
        alignItems: "center",
      }}
    >
      <IconButton
        size="small"
        onClick={(event) =>
          onPageChange(event, 0)
        }
        disabled={page === 0}
      >
        {theme.direction === "rtl" ? (
          <LastPageIcon />
        ) : (
          <FirstPageIcon />
        )}
      </IconButton>

      <IconButton
        size="small"
        onClick={(event) =>
          onPageChange(event, page - 1)
        }
        disabled={page === 0}
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>

      <IconButton
        size="small"
        onClick={(event) =>
          onPageChange(event, page + 1)
        }
        disabled={page >= lastPage}
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>

      <IconButton
        size="small"
        onClick={(event) =>
          onPageChange(event, lastPage)
        }
        disabled={page >= lastPage}
      >
        {theme.direction === "rtl" ? (
          <FirstPageIcon />
        ) : (
          <LastPageIcon />
        )}
      </IconButton>
    </Box>
  );
}

async function loadDeviceData(
  filters: Filters,
): Promise<DeviceAttendanceRow[]> {
  const payload = {
    data: {
      startDate: filters.startDate
        ? dayjs(filters.startDate).format("YYYY-MM-DD")
        : null,

      endDate: filters.endDate
        ? dayjs(filters.endDate).format("YYYY-MM-DD")
        : null,

      organization:
        filters.organization || null,

      personId:
        filters.personId.trim() || null,

      personName:
        filters.personName.trim() || null,
    },
  };

  const response = await GetDeviceData(payload);

  const result =
    response?.data?.data ??
    response?.data ??
    {};

  const records = Array.isArray(result?.result)
    ? result.result
    : [];

  return records.map((item: any) => {
    const rawDate = String(
      item?.date ??
      item?.Date ??
      "",
    ).trim();

    const rawTime = String(
      item?.time ??
      item?.Time ??
      "",
    ).trim();

    const personId = String(
      item?.id ??
      item?.ID ??
      "",
    ).trim();

    const personName = String(
      item?.personName ??
      item?.PersonName ??
      "",
    ).trim();

    const organization = String(
      item?.organization ??
      item?.Organization ??
      "",
    ).trim();

    const dateOnly =
      rawDate.length >= 10
        ? rawDate.substring(0, 10)
        : rawDate;

    let formattedDate = dateOnly;

    const parsedDate = dayjs(
      dateOnly,
      "YYYY-MM-DD",
      true,
    );

    if (parsedDate.isValid()) {
      formattedDate =
        parsedDate.format("D, MMM, YYYY");
    }

    const normalizedTime =
      rawTime.length >= 8
        ? rawTime.substring(0, 8)
        : rawTime;

    return {
      personId,
      personName,
      organization,
      date: formattedDate,
      time: normalizedTime,
    };
  });
}

async function importDeviceData(
  file: File,
): Promise<void> {
  const response = await ImportDeviceData(file);

  const result =
    response?.data?.data ??
    response?.data ??
    {};

  const success =
    result?.success ??
    result?.Success ??
    false;

  const message =
    result?.message ??
    result?.Message ??
    "";

  if (!success) {
    throw new Error(
      message || "Device data import failed.",
    );
  }
}

const DeviceData: React.FC = () => {
  const theme = useTheme();

  const fileInputRef =
    useRef<HTMLInputElement | null>(null);

  const [mode, setMode] = useState<
    "device" | "import"
  >("device");

  const [filters, setFilters] =
    useState<Filters>(INITIAL_FILTERS);

  const [rows, setRows] =
    useState<DeviceAttendanceRow[]>([]);

  const [loading, setLoading] =
    useState(false);

  const [tableSearch, setTableSearch] =
    useState("");

  const [page, setPage] =
    useState(0);

  const [rowsPerPage, setRowsPerPage] =
    useState(10);

  const [showAllFilters, setShowAllFilters] =
    useState(false);

  const [columnAnchorEl, setColumnAnchorEl] =
    useState<null | HTMLElement>(null);

  const [visibleColumns, setVisibleColumns] =
    useState<
      Record<
        keyof DeviceAttendanceRow,
        boolean
      >
    >({
      personId: true,
      personName: true,
      organization: true,
      date: true,
      time: true,
    });

  const [searchText, setSearchText] =
    useState<
      Partial<
        Record<
          keyof DeviceAttendanceRow,
          string
        >
      >
    >({});

  const [orderDirection, setOrderDirection] =
    useState<"asc" | "desc">("asc");

  const [valueToOrderBy, setValueToOrderBy] =
    useState<keyof DeviceAttendanceRow>(
      "personId",
    );

  const [importedFile, setImportedFile] =
    useState<File | null>(null);

  const [importing, setImporting] =
    useState(false);

  const [isDragging, setIsDragging] =
    useState(false);

  const [
    organizations,
    setOrganizations,
  ] = useState<string[]>([]);

  const [
    organizationsLoading,
    setOrganizationsLoading,
  ] = useState(false);

  const [
    organizationsLoaded,
    setOrganizationsLoaded,
  ] = useState(false);

  const updateFilter = (
    key: keyof Filters,
    value: string | Date | null,
  ) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));

    setPage(0);
  };

  const handleOrganizationOpen =
    async () => {
      if (
        organizationsLoaded ||
        organizationsLoading
      ) {
        return;
      }

      setOrganizationsLoading(true);

      try {
        const response =
          await GetOrganizations();

        const result =
          response?.data?.data ??
          response?.data ??
          [];

        const organizationList =
          Array.isArray(result)
            ? result.filter(
              (
                organization,
              ): organization is string =>
                typeof organization ===
                "string" &&
                organization.trim() !==
                "" &&
                organization.trim() !==
                "0",
            )
            : [];

        setOrganizations(
          organizationList,
        );

        setOrganizationsLoaded(true);
      } catch (error) {
        console.error(
          "Failed to load organizations:",
          error,
        );

        toast.error(
          "Failed to load organizations.",
        );
      } finally {
        setOrganizationsLoading(false);
      }
    };

  const hasColumnFilters =
    Object.values(searchText).some(
      (value) =>
        value !== null &&
        value !== undefined &&
        String(value).trim() !== "",
    );

  const columnFilterCount =
    Object.values(searchText).filter(
      (value) =>
        value !== null &&
        value !== undefined &&
        String(value).trim() !== "",
    ).length;

  const filteredRows = useMemo(() => {
    let data = [...rows];

    const global =
      tableSearch.trim().toLowerCase();

    if (global) {
      data = data.filter((row) =>
        DEVICE_COLUMNS.some(
          ([, field]) =>
            String(row[field] ?? "")
              .toLowerCase()
              .includes(global),
        ),
      );
    }

    Object.entries(searchText).forEach(
      ([field, value]) => {
        if (!value?.trim()) {
          return;
        }

        data = data.filter((row) =>
          String(
            row[
            field as keyof DeviceAttendanceRow
            ] ?? "",
          )
            .toLowerCase()
            .includes(
              value.toLowerCase().trim(),
            ),
        );
      },
    );

    data.sort((first, second) => {
      const firstValue = String(
        first[valueToOrderBy] ?? "",
      );

      const secondValue = String(
        second[valueToOrderBy] ?? "",
      );

      const comparison =
        firstValue.localeCompare(
          secondValue,
          undefined,
          {
            numeric: true,
            sensitivity: "base",
          },
        );

      return orderDirection === "asc"
        ? comparison
        : -comparison;
    });

    return data;
  }, [
    rows,
    tableSearch,
    searchText,
    valueToOrderBy,
    orderDirection,
  ]);

  const visibleRows = useMemo(
    () =>
      filteredRows.slice(
        page * rowsPerPage,
        page * rowsPerPage +
        rowsPerPage,
      ),
    [
      filteredRows,
      page,
      rowsPerPage,
    ],
  );

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(
        filteredRows.length /
        rowsPerPage,
      ) - 1,
    );

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [
    filteredRows.length,
    page,
    rowsPerPage,
  ]);

  const handleSort = (
    field: keyof DeviceAttendanceRow,
  ) => {
    const isAsc =
      valueToOrderBy === field &&
      orderDirection === "asc";

    setOrderDirection(
      isAsc ? "desc" : "asc",
    );

    setValueToOrderBy(field);
    setPage(0);
  };

  const resetFilter = () => {
    setFilters(INITIAL_FILTERS);
    setTableSearch("");
    setSearchText({});
    setShowAllFilters(false);
    setPage(0);
  };

  const clearColumnFilter = (
    field: keyof DeviceAttendanceRow,
  ) => {
    setSearchText((previous) => {
      const updated = {
        ...previous,
      };

      delete updated[field];

      return updated;
    });

    setPage(0);
  };

  const isColumnFiltered = (
    field: keyof DeviceAttendanceRow,
  ) =>
    Boolean(
      searchText[field]?.trim(),
    );

  const resetTableFilters = () => {
    setTableSearch("");
    setSearchText({});
    setShowAllFilters(false);
    setPage(0);
  };

  const handleSearch = async () => {
    setLoading(true);

    try {
      const result =
        await loadDeviceData(
          filters,
        );

      setRows(
        Array.isArray(result)
          ? result
          : [],
      );

      setPage(0);
    } catch (error) {
      console.error(
        "Failed to load ASAI device data:",
        error,
      );

      setRows([]);

      toast.error(
        "Failed to load device data.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFormat =
    async () => {
      try {
        const workbook =
          new ExcelJS.Workbook();

        const worksheet =
          workbook.addWorksheet(
            "Sheet1",
          );

        worksheet.columns = [
          {
            header: "Person_ID",
            key: "personId",
            width: 16,
          },
          {
            header: "Person_Name",
            key: "personName",
            width: 28,
          },
          {
            header: "Datetime",
            key: "datetime",
            width: 24,
          },
          {
            header: "Organization",
            key: "organization",
            width: 30,
          },
        ];

        worksheet.addRow({
          personId: "8123",
          personName: "Jay Rana",
          datetime:
            new Date(
              "2026-09-28T07:47:49",
            ),
          organization:
            "Office Employee",
        });

        worksheet.addRow({
          personId: "8063",
          personName:
            "Bhargav Shukla",
          datetime:
            new Date(
              "2026-09-29T09:01:39",
            ),
          organization:
            "Office Employee",
        });

        worksheet.getColumn(
          "datetime",
        ).numFmt =
          "dd-mm-yyyy hh:mm:ss";

        const organizationOptions = [
          "Apprentice",
          "Contract CNC",
          "Contract Flex",
          "Employee",
          "Office Employee",
          "Office Employee_SuperVisor",
          "Payroll CNC",
          "Payroll Flex",
        ];

        const organizationSheet =
          workbook.addWorksheet(
            "OrganizationList",
          );

        organizationOptions.forEach(
          (organization, index) => {
            organizationSheet.getCell(
              index + 1,
              1,
            ).value = organization;
          },
        );

        organizationSheet.state =
          "hidden";

        for (
          let row = 2;
          row <= 500;
          row++
        ) {
          worksheet.getCell(
            row,
            4,
          ).dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: [
              "'OrganizationList'!$A$1:$A$8",
            ],
            showErrorMessage: true,
            errorTitle:
              "Invalid Organization",
            error:
              "Please select an organization from the dropdown.",
          };
        }

        worksheet.getRow(
          1,
        ).font = {
          bold: true,
        };

        worksheet.getRow(
          1,
        ).alignment = {
          vertical: "middle",
        };

        worksheet.views = [
          {
            state: "frozen",
            ySplit: 1,
          },
        ];

        const buffer =
          await workbook.xlsx.writeBuffer();

        const blob = new Blob(
          [buffer],
          {
            type:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        );

        const url =
          URL.createObjectURL(
            blob,
          );

        const link =
          document.createElement(
            "a",
          );

        link.href = url;
        link.download =
          "DataFromDevice.xlsx";

        document.body.appendChild(
          link,
        );

        link.click();

        document.body.removeChild(
          link,
        );

        URL.revokeObjectURL(url);
      } catch (error) {
        console.error(
          "Failed to generate Excel format:",
          error,
        );

        toast.error(
          "Failed to download Excel format.",
        );
      }
    };

  const selectImportFile = (
    file: File | null,
  ) => {
    if (!file) {
      return;
    }

    const extension =
      file.name
        .split(".")
        .pop()
        ?.toLowerCase();

    if (extension !== "xlsx") {
      toast.error(
        "Only .xlsx Excel files are supported.",
      );

      setImportedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value =
          "";
      }

      return;
    }

    if (file.size === 0) {
      toast.error(
        "The selected file is empty.",
      );

      setImportedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value =
          "";
      }

      return;
    }

    setImportedFile(file);
  };

  const handleChooseFile = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    selectImportFile(
      event.target.files?.[0] ||
      null,
    );
  };

  const handleDragEnter = (
    event: React.DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (!importing) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (
    event: React.DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (!importing) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (
    event: React.DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragging(false);
  };

  const handleDrop = (
    event: React.DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragging(false);

    if (importing) {
      return;
    }

    const file =
      event.dataTransfer.files?.[0] ||
      null;

    selectImportFile(file);
  };

  const handleRemoveImportedFile =
    () => {
      setImportedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value =
          "";
      }
    };

  const formatFileSize = (
    bytes: number,
  ) => {
    if (bytes < 1024) {
      return `${bytes} B`;
    }

    if (bytes < 1024 * 1024) {
      return `${(
        bytes / 1024
      ).toFixed(1)} KB`;
    }

    return `${(
      bytes /
      (1024 * 1024)
    ).toFixed(1)} MB`;
  };

  const handleImport = async () => {
    if (!importedFile) {
      toast.error(
        "Please select an Excel file first.",
      );

      return;
    }

    setImporting(true);

    try {
      await importDeviceData(
        importedFile,
      );

      toast.success(
        "Device data imported successfully.",
      );

      setImportedFile(null);
      setIsDragging(false);

      if (fileInputRef.current) {
        fileInputRef.current.value =
          "";
      }
    } catch (error: any) {
      console.error(
        "Failed to import ASAI device data:",
        error,
      );

      const errorMessage =
        error?.response?.data
          ?.message ||
        error?.response?.data
          ?.Message ||
        error?.message ||
        "Device data import failed.";

      toast.error(errorMessage);
    } finally {
      setImporting(false);
    }
  };

  const activeColumnCount =
    Object.values(
      visibleColumns,
    ).filter(Boolean).length;

  return (
    <Box
      className="device-data-page"
      sx={{
        width: "100%",
        bgcolor:
          "background.default",
      }}
    >
      <PageContainer title="Data From Device">
        <Breadcrumb
          title="Data From Device"
          items={BCrumb}
        />

        <Card
          sx={{
            borderRadius: 2.5,
          }}
        >
          <CardContent
            sx={{
              p: {
                xs: 1.5,
                sm: 2,
                md: 2.5,
              },
              "&:last-child": {
                pb: {
                  xs: 1.5,
                  sm: 2,
                  md: 2.5,
                },
              },
            }}
          >
            {/* Tabs */}
            <Box
              sx={{
                borderBottom: 1,
                borderColor:
                  "divider",
                mb: 2.5,
              }}
            >
              <Box
                role="tablist"
                sx={{
                  position:
                    "relative",
                  display: "flex",
                  width: "fit-content",
                  minHeight: 44,
                }}
              >
                <Tab
                  value="device"
                  label="Data From Device"
                  onClick={() => {
                    setMode("device");
                    setImportedFile(
                      null,
                    );
                    setIsDragging(
                      false,
                    );
                    setSearchText({});
                    setTableSearch(
                      "",
                    );
                    setPage(0);

                    if (
                      fileInputRef.current
                    ) {
                      fileInputRef.current.value =
                        "";
                    }
                  }}
                  sx={{
                    width: {
                      xs: 145,
                      sm: 160,
                    },
                    minWidth: {
                      xs: 145,
                      sm: 160,
                    },
                    minHeight: 44,
                    px: 1.5,
                    textTransform:
                      "none",
                    fontSize:
                      "0.875rem",
                    fontWeight:
                      mode ===
                        "device"
                        ? 700
                        : 600,
                    color:
                      mode ===
                        "device"
                        ? "text.primary"
                        : "text.secondary",
                    opacity: 1,
                    "&:hover": {
                      color:
                        "text.primary",
                      backgroundColor:
                        "action.hover",
                    },
                  }}
                />

                <Tab
                  value="import"
                  label="Import Data"
                  onClick={() => {
                    setMode("import");
                    setImportedFile(
                      null,
                    );
                    setIsDragging(
                      false,
                    );
                    setSearchText({});
                    setTableSearch(
                      "",
                    );
                    setPage(0);

                    if (
                      fileInputRef.current
                    ) {
                      fileInputRef.current.value =
                        "";
                    }
                  }}
                  sx={{
                    width: {
                      xs: 145,
                      sm: 160,
                    },
                    minWidth: {
                      xs: 145,
                      sm: 160,
                    },
                    minHeight: 44,
                    px: 1.5,
                    textTransform:
                      "none",
                    fontSize:
                      "0.875rem",
                    fontWeight:
                      mode ===
                        "import"
                        ? 700
                        : 600,
                    color:
                      mode ===
                        "import"
                        ? "text.primary"
                        : "text.secondary",
                    opacity: 1,
                    "&:hover": {
                      color:
                        "text.primary",
                      backgroundColor:
                        "action.hover",
                    },
                  }}
                />

                <Box
                  aria-hidden="true"
                  sx={{
                    position:
                      "absolute",
                    left: 0,
                    bottom: 0,
                    width: {
                      xs: 145,
                      sm: 160,
                    },
                    height: 3,
                    borderRadius:
                      "3px 3px 0 0",
                    bgcolor:
                      "primary.main",
                    transform:
                      mode ===
                        "device"
                        ? "translateX(0)"
                        : {
                          xs:
                            "translateX(145px)",
                          sm:
                            "translateX(160px)",
                        },
                    transition:
                      "transform 260ms cubic-bezier(0.4, 0, 0.2, 1)",
                    pointerEvents:
                      "none",
                  }}
                />
              </Box>
            </Box>

            {/* =========================
                IMPORT TAB
               ========================= */}
            {mode === "import" ? (
              <Box
                sx={{
                  width: "100%",
                }}
              >
                <Stack
                  spacing={2}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      display:
                        "flex",
                      alignItems: {
                        xs: "flex-start",
                        sm: "center",
                      },
                      justifyContent:
                        "space-between",
                      gap: 1.5,
                      flexWrap:
                        "wrap",
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize:
                          {
                            xs:
                              "0.98rem",
                            sm:
                              "1.05rem",
                          },
                          fontWeight: 700,
                          color:
                            "text.primary",
                        }}
                      >
                        Import device attendance
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 0.35,
                        }}
                      >
                        Upload the ASAI device Excel file using the required format.
                      </Typography>
                    </Box>

                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        <FileDownloadIcon />
                      }
                      onClick={
                        handleDownloadFormat
                      }
                      disabled={
                        importing
                      }
                      sx={{
                        textTransform:
                          "none",
                        borderRadius:
                          1.5,
                        minHeight: 36,
                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      Download format
                    </Button>
                  </Box>

                  {/* Hidden input */}
                  <input
                    ref={fileInputRef}
                    hidden
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={
                      handleChooseFile
                    }
                  />

                  {/* Drag and drop area */}
                  <Paper
                    elevation={0}
                    onDragEnter={
                      handleDragEnter
                    }
                    onDragOver={
                      handleDragOver
                    }
                    onDragLeave={
                      handleDragLeave
                    }
                    onDrop={handleDrop}
                    onClick={() => {
                      if (
                        !importing
                      ) {
                        fileInputRef.current?.click();
                      }
                    }}
                    sx={{
                      position:
                        "relative",
                      width:
                        "100%",
                      minHeight: {
                        xs: 225,
                        sm: 255,
                      },
                      border:
                        "1.5px dashed",
                      borderColor:
                        isDragging
                          ? "primary.main"
                          : importedFile
                            ? "success.main"
                            : "divider",
                      bgcolor:
                        isDragging
                          ? "action.hover"
                          : importedFile
                            ? "success.light"
                            : "background.paper",
                      borderRadius:
                        2.5,
                      display:
                        "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "center",
                      px: {
                        xs: 2,
                        sm: 4,
                      },
                      py: 3,
                      cursor:
                        importing
                          ? "default"
                          : "pointer",
                      transition:
                        "border-color 180ms ease, background-color 180ms ease, box-shadow 180ms ease",
                      "&:hover":
                        !importing
                          ? {
                            borderColor:
                              importedFile
                                ? "success.main"
                                : "primary.main",
                          }
                          : undefined,
                    }}
                  >
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      spacing={1}
                      sx={{
                        width:
                          "100%",
                        maxWidth:
                          650,
                      }}
                    >
                      {/* Icon */}
                      <Box
                        sx={{
                          width: 62,
                          height: 62,
                          borderRadius:
                            "50%",
                          display:
                            "flex",
                          alignItems:
                            "center",
                          justifyContent:
                            "center",
                          bgcolor:
                            isDragging
                              ? "primary.main"
                              : importedFile
                                ? "success.main"
                                : "action.hover",
                          color:
                            isDragging ||
                              importedFile
                              ? "common.white"
                              : "primary.main",
                          transition:
                            "all 180ms ease",
                        }}
                      >
                        <CloudUploadIcon
                          sx={{
                            fontSize:
                              31,
                          }}
                        />
                      </Box>

                      {importedFile ? (
                        <>
                          <Typography
                            sx={{
                              mt: 0.5,
                              fontSize:
                              {
                                xs:
                                  "0.98rem",
                                sm:
                                  "1.05rem",
                              },
                              fontWeight: 700,
                              textAlign:
                                "center",
                              color:
                                "text.primary",
                              maxWidth:
                                "100%",
                              wordBreak:
                                "break-word",
                            }}
                          >
                            {importedFile.name}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              textAlign:
                                "center",
                            }}
                          >
                            {formatFileSize(
                              importedFile.size,
                            )}{" "}
                            · Excel workbook
                          </Typography>

                          <Stack
                            direction={{
                              xs:
                                "column",
                              sm:
                                "row",
                            }}
                            spacing={1}
                            sx={{
                              mt: 0.5,
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                <CloudUploadIcon />
                              }
                              onClick={(
                                event,
                              ) => {
                                event.stopPropagation();

                                fileInputRef.current?.click();
                              }}
                              disabled={
                                importing
                              }
                              sx={{
                                textTransform:
                                  "none",
                                borderRadius:
                                  1.5,
                              }}
                            >
                              Choose another file
                            </Button>

                            <Button
                              size="small"
                              variant="text"
                              color="inherit"
                              onClick={(
                                event,
                              ) => {
                                event.stopPropagation();

                                handleRemoveImportedFile();
                              }}
                              disabled={
                                importing
                              }
                              startIcon={
                                <CloseIcon />
                              }
                              sx={{
                                textTransform:
                                  "none",
                                borderRadius:
                                  1.5,
                              }}
                            >
                              Remove
                            </Button>
                          </Stack>
                        </>
                      ) : (
                        <>
                          <Typography
                            sx={{
                              mt: 0.5,
                              fontSize:
                              {
                                xs:
                                  "1rem",
                                sm:
                                  "1.1rem",
                              },
                              fontWeight: 700,
                              color:
                                "text.primary",
                              textAlign:
                                "center",
                            }}
                          >
                            {isDragging
                              ? "Drop your Excel file here"
                              : "Drag & drop your Excel file here"}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              textAlign:
                                "center",
                            }}
                          >
                            or click anywhere in this area to browse
                          </Typography>

                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              <CloudUploadIcon />
                            }
                            onClick={(
                              event,
                            ) => {
                              event.stopPropagation();

                              fileInputRef.current?.click();
                            }}
                            disabled={
                              importing
                            }
                            sx={{
                              mt: 0.5,
                              textTransform:
                                "none",
                              borderRadius:
                                1.5,
                            }}
                          >
                            Browse files
                          </Button>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              mt: 0.25,
                            }}
                          >
                            Supported format: .xlsx
                          </Typography>
                        </>
                      )}
                    </Stack>
                  </Paper>

                  {/* Bottom information + import */}
                  <Box
                    sx={{
                      display:
                        "flex",
                      alignItems: {
                        xs:
                          "flex-start",
                        sm:
                          "center",
                      },
                      justifyContent:
                        "space-between",
                      gap: 1.5,
                      flexWrap:
                        "wrap",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth:
                          240,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display:
                            "block",
                          lineHeight: 1.5,
                        }}
                      >
                        Required columns
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.15,
                          fontWeight: 600,
                          color:
                            "text.primary",
                        }}
                      >
                        Person_ID · Person_Name · Datetime · Organization
                      </Typography>
                    </Box>

                    <Button
                      size="small"
                      variant="contained"
                      onClick={
                        handleImport
                      }
                      disabled={
                        !importedFile ||
                        importing
                      }
                      startIcon={
                        importing ? (
                          <CircularProgress
                            size={16}
                            thickness={5}
                            color="inherit"
                          />
                        ) : (
                          <CloudUploadIcon />
                        )
                      }
                      sx={{
                        minWidth: {
                          xs:
                            "100%",
                          sm:
                            130,
                        },
                        minHeight:
                          38,
                        borderRadius:
                          1.5,
                        textTransform:
                          "none",
                        fontWeight: 700,
                      }}
                    >
                      {importing
                        ? "Importing..."
                        : "Import Data"}
                    </Button>
                  </Box>

                  {/* Info */}
                  <Box
                    sx={{
                      display:
                        "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "center",
                      px: 1.5,
                      py: 1,
                      borderRadius:
                        1.5,
                      bgcolor:
                        "action.hover",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        textAlign:
                          "center",
                      }}
                    >
                      Imported records use the standard ASAI device-import defaults for Status, Device, DeviceNo and CardNumber.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ) : (
              /* =========================
                 DEVICE DATA TAB
                 ========================= */
              <>
                {/* Main filters */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                    {
                      xs: "1fr",
                      lg:
                        "minmax(145px,1fr) minmax(145px,1fr) minmax(190px,1.25fr) minmax(170px,1fr) minmax(170px,1fr) auto",
                    },
                    gap: 2,
                    alignItems: "end",
                    mb: 2,
                    width:
                      "100%",
                  }}
                >
                  {/* Start Date */}
                  <Box
                    sx={{
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      Start Date
                    </Typography>

                    <Box
                      sx={{
                        mt: 0.5,
                        width:
                          "100%",
                      }}
                    >
                      <DatePicker
                        selected={
                          filters.startDate
                        }
                        onChange={(
                          date: Date | null,
                        ) =>
                          updateFilter(
                            "startDate",
                            date,
                          )
                        }
                        dateFormat="dd/MM/yyyy"
                        placeholderText="dd-mm-yyyy"
                        popperPlacement="bottom-start"
                        popperClassName="device-date-popper"
                        customInput={
                          <TextField
                            fullWidth
                            size="small"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <CalendarTodayIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        }
                      />
                    </Box>
                  </Box>

                  {/* End Date */}
                  <Box
                    sx={{
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      End Date
                    </Typography>

                    <Box
                      sx={{
                        mt: 0.5,
                        width:
                          "100%",
                      }}
                    >
                      <DatePicker
                        selected={
                          filters.endDate
                        }
                        onChange={(
                          date: Date | null,
                        ) =>
                          updateFilter(
                            "endDate",
                            date,
                          )
                        }
                        dateFormat="dd/MM/yyyy"
                        placeholderText="dd-mm-yyyy"
                        popperPlacement="bottom-start"
                        popperClassName="device-date-popper"
                        customInput={
                          <TextField
                            fullWidth
                            size="small"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <CalendarTodayIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        }
                      />
                    </Box>
                  </Box>

                  {/* Organization */}
                  <Box
                    sx={{
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      Organization
                    </Typography>

                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        mt: 0.5,
                      }}
                    >
                      <Select
                        displayEmpty
                        value={
                          filters.organization
                        }
                        onOpen={
                          handleOrganizationOpen
                        }
                        onChange={(
                          event,
                        ) =>
                          updateFilter(
                            "organization",
                            event.target
                              .value,
                          )
                        }
                      >
                        <MenuItem value="">
                          -- All Organization --
                        </MenuItem>

                        {organizationsLoading ? (
                          <MenuItem
                            disabled
                          >
                            Loading organizations...
                          </MenuItem>
                        ) : (
                          organizations.map(
                            (
                              organization,
                            ) => (
                              <MenuItem
                                key={
                                  organization
                                }
                                value={
                                  organization
                                }
                              >
                                {
                                  organization
                                }
                              </MenuItem>
                            ),
                          )
                        )}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Person ID */}
                  <Box
                    sx={{
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      Person ID
                    </Typography>

                    <TextField
                      fullWidth
                      size="small"
                      sx={{
                        mt: 0.5,
                      }}
                      value={
                        filters.personId
                      }
                      placeholder="Person ID"
                      onChange={(
                        event,
                      ) =>
                        updateFilter(
                          "personId",
                          event.target
                            .value,
                        )
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Person Name */}
                  <Box
                    sx={{
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      Person Name
                    </Typography>

                    <TextField
                      fullWidth
                      size="small"
                      sx={{
                        mt: 0.5,
                      }}
                      value={
                        filters.personName
                      }
                      placeholder="Person Name"
                      onChange={(
                        event,
                      ) =>
                        updateFilter(
                          "personName",
                          event.target
                            .value,
                        )
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Buttons */}
                  <Box
                    sx={{
                      display:
                        "flex",
                      alignItems:
                        "flex-end",
                      gap: 1,
                      height:
                        "100%",
                      whiteSpace:
                        "nowrap",
                      pb: 0.5,
                    }}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={
                        <SearchRoundedIcon />
                      }
                      onClick={
                        handleSearch
                      }
                      disabled={
                        loading
                      }
                      sx={{
                        textTransform:
                          "none",
                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      {loading
                        ? "Searching..."
                        : "Search"}
                    </Button>

                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        <RestartAltIcon />
                      }
                      onClick={
                        resetFilter
                      }
                      sx={{
                        textTransform:
                          "none",
                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      Reset
                    </Button>
                  </Box>
                </Box>

                {/* Table toolbar */}
                <Box
                  sx={{
                    display:
                      "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "flex-end",
                    gap: 0.75,
                    mb: 1,
                    flexWrap:
                      "wrap",
                    width:
                      "100%",
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={
                      tableSearch
                    }
                    onChange={(
                      event,
                    ) => {
                      setTableSearch(
                        event.target
                          .value,
                      );

                      setPage(0);
                    }}
                    sx={{
                      width: {
                        xs: "100%",
                        md: 200,
                      },
                      backgroundColor:
                        "white",
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        setShowAllFilters(
                          (current) =>
                            !current,
                        );

                        setTableSearch(
                          "",
                        );

                        setPage(0);
                      }}
                    >
                      <FilterAltIcon
                        sx={{
                          color:
                            showAllFilters
                              ? "primary.main"
                              : "text.primary",
                        }}
                      />
                    </IconButton>

                    {hasColumnFilters &&
                      !showAllFilters && (
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{
                            textTransform:
                              "none",
                            display:
                              "flex",
                            alignItems:
                              "center",
                            gap: 0.5,
                            whiteSpace:
                              "nowrap",
                          }}
                          onClick={() =>
                            setShowAllFilters(
                              true,
                            )
                          }
                        >
                          Filters (
                          {
                            columnFilterCount
                          }
                          )
                          <CloseIcon
                            sx={{
                              fontSize: 16,
                            }}
                            onClick={(
                              event,
                            ) => {
                              event.stopPropagation();

                              setTableSearch(
                                "",
                              );

                              setSearchText(
                                {},
                              );

                              setShowAllFilters(
                                false,
                              );

                              setPage(0);
                            }}
                          />
                        </Button>
                      )}
                  </Box>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      <VisibilityIcon />
                    }
                    onClick={(
                      event,
                    ) =>
                      setColumnAnchorEl(
                        event.currentTarget,
                      )
                    }
                    sx={{
                      textTransform:
                        "none",
                    }}
                  >
                    Columns
                    {
                      Object.values(
                        visibleColumns,
                      ).filter(
                        (visible) =>
                          !visible,
                      ).length >
                      0 && (
                        <Box
                          component="span"
                          sx={{
                            ml: 0.5,
                            color:
                              "primary.main",
                          }}
                        >
                          (
                          {
                            Object.values(
                              visibleColumns,
                            ).filter(
                              (
                                visible,
                              ) =>
                                !visible,
                            ).length
                          }
                          )
                        </Box>
                      )
                    }
                  </Button>

                  <Menu
                    anchorEl={
                      columnAnchorEl
                    }
                    open={Boolean(
                      columnAnchorEl,
                    )}
                    onClose={() =>
                      setColumnAnchorEl(
                        null,
                      )
                    }
                  >
                    {DEVICE_COLUMNS.map(
                      ([
                        label,
                        field,
                      ]) => (
                        <MenuItem
                          key={field}
                          onClick={() => {
                            if (
                              visibleColumns[
                              field
                              ] &&
                              activeColumnCount ===
                              1
                            ) {
                              return;
                            }

                            setVisibleColumns(
                              (
                                previous,
                              ) => ({
                                ...previous,
                                [field]:
                                  !previous[
                                  field
                                  ],
                              }),
                            );
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={
                              visibleColumns[
                              field
                              ]
                            }
                          />

                          <ListItemText
                            primary={
                              label
                            }
                          />
                        </MenuItem>
                      ),
                    )}
                  </Menu>

                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      !tableSearch.trim() &&
                      !hasColumnFilters
                    }
                    onClick={
                      resetTableFilters
                    }
                    sx={{
                      textTransform:
                        "none",
                    }}
                  >
                    Reset Filter
                  </Button>
                </Box>

                {/* Table */}
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight:
                      showAllFilters
                        ? 540
                        : 440,
                    overflowY:
                      filteredRows.length >
                        5
                        ? "auto"
                        : "visible",
                    overflowX:
                      "auto",
                    scrollbarGutter:
                      "stable",
                  }}
                >
                  <Table
                    stickyHeader
                    aria-label="device attendance table"
                    sx={{
                      whiteSpace:
                        "nowrap",
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {DEVICE_COLUMNS.filter(
                          ([, field]) =>
                            visibleColumns[
                            field
                            ],
                        ).map(
                          ([
                            label,
                            field,
                          ]) => (
                            <TableCell
                              key={
                                field
                              }
                            >
                              <TableSortLabel
                                active={
                                  valueToOrderBy ===
                                  field
                                }
                                direction={
                                  valueToOrderBy ===
                                    field
                                    ? orderDirection
                                    : "desc"
                                }
                                onClick={() =>
                                  handleSort(
                                    field,
                                  )
                                }
                              >
                                {label}
                              </TableSortLabel>
                            </TableCell>
                          ),
                        )}
                      </TableRow>

                      {showAllFilters && (
                        <TableRow>
                          {DEVICE_COLUMNS.filter(
                            ([, field]) =>
                              visibleColumns[
                              field
                              ],
                          ).map(
                            ([, field]) => (
                              <TableCell
                                key={
                                  field
                                }
                              >
                                <TextField
                                  size="small"
                                  fullWidth
                                  placeholder="Search..."
                                  value={
                                    searchText[
                                    field
                                    ] ||
                                    ""
                                  }
                                  onChange={(
                                    event,
                                  ) => {
                                    const value =
                                      event
                                        .target
                                        .value;

                                    setSearchText(
                                      (
                                        previous,
                                      ) => {
                                        const updated =
                                        {
                                          ...previous,
                                        };

                                        if (
                                          value.trim()
                                        ) {
                                          updated[
                                            field
                                          ] =
                                            value;
                                        } else {
                                          delete updated[
                                            field
                                          ];
                                        }

                                        return updated;
                                      },
                                    );

                                    setPage(
                                      0,
                                    );
                                  }}
                                  InputProps={{
                                    endAdornment:
                                      isColumnFiltered(
                                        field,
                                      ) ? (
                                        <InputAdornment position="end">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              clearColumnFilter(
                                                field,
                                              )
                                            }
                                          >
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                        </InputAdornment>
                                      ) : undefined,
                                  }}
                                />
                              </TableCell>
                            ),
                          )}
                        </TableRow>
                      )}
                    </TableHead>

                    <TableBody>
                      {loading && (
                        <TableRow>
                          <TableCell
                            colSpan={Math.max(
                              activeColumnCount,
                              1,
                            )}
                            align="center"
                            sx={{
                              py: 5,
                            }}
                          >
                            <CircularProgress
                              size={28}
                            />
                          </TableCell>
                        </TableRow>
                      )}

                      {!loading &&
                        filteredRows.length ===
                        0 && (
                          <TableRow>
                            <TableCell
                              colSpan={Math.max(
                                activeColumnCount,
                                1,
                              )}
                              align="center"
                              sx={{
                                py: 4,
                                color:
                                  "text.secondary",
                              }}
                            >
                              No data found
                            </TableCell>
                          </TableRow>
                        )}

                      {!loading &&
                        visibleRows.map(
                          (
                            row,
                            index,
                          ) => (
                            <TableRow
                              key={`${row.personId}-${row.date}-${row.time}-${index}`}
                              hover
                            >
                              {visibleColumns.personId && (
                                <TableCell>
                                  {
                                    row.personId
                                  }
                                </TableCell>
                              )}

                              {visibleColumns.personName && (
                                <TableCell>
                                  {
                                    row.personName
                                  }
                                </TableCell>
                              )}

                              {visibleColumns.organization && (
                                <TableCell>
                                  {
                                    row.organization
                                  }
                                </TableCell>
                              )}

                              {visibleColumns.date && (
                                <TableCell>
                                  {
                                    row.date
                                  }
                                </TableCell>
                              )}

                              {visibleColumns.time && (
                                <TableCell>
                                  {
                                    row.time
                                  }
                                </TableCell>
                              )}
                            </TableRow>
                          ),
                        )}
                    </TableBody>

                    <TableFooter>
                      <TableRow>
                        <TableCell
                          colSpan={Math.max(
                            activeColumnCount,
                            1,
                          )}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>

                <Box
                  sx={{
                    display:
                      "flex",
                    justifyContent:
                      "flex-end",
                  }}
                >
                  <TablePagination
                    rowsPerPageOptions={
                      PAGE_SIZE_OPTIONS
                    }
                    count={
                      filteredRows.length
                    }
                    rowsPerPage={
                      rowsPerPage
                    }
                    page={page}
                    onPageChange={(
                      _,
                      newPage,
                    ) =>
                      setPage(
                        newPage,
                      )
                    }
                    onRowsPerPageChange={(
                      event,
                    ) => {
                      setRowsPerPage(
                        parseInt(
                          event.target
                            .value,
                          10,
                        ),
                      );

                      setPage(0);
                    }}
                    ActionsComponent={
                      TablePaginationActions
                    }
                  />
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </PageContainer>

      <style>{`
        .device-date-popper {
          z-index: 1500 !important;
        }

        .device-date-popper .react-datepicker {
          z-index: 1500 !important;
          font-family: inherit;
        }

        .device-date-popper .react-datepicker__triangle {
          display: none;
        }

        @media print {
          body * {
            visibility: hidden !important;
          }

          .device-data-page,
          .device-data-page * {
            visibility: visible !important;
          }

          .device-data-page {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            background: #fff !important;
          }

          .device-data-page .MuiButton-root,
          .device-data-page .MuiTextField-root,
          .device-data-page .MuiTablePagination-root,
          .device-data-page .MuiMenu-root {
            display: none !important;
          }
        }
      `}</style>
    </Box>
  );
};

export default DeviceData;